import { rank, route, type Finding } from '@/shared/ui/council'
import type {
  AnalyticsSummary, GeographyRow, ResponseTimes, SourceRow, TrendPoint,
} from '../api'
import { label } from '../vocab'

export type { Finding }

export interface Board {
  summary: AnalyticsSummary
  trend: TrendPoint[]
  geography: GeographyRow[]
  sources: SourceRow[]
  times: ResponseTimes | null
  days: number
}

/**
 * The incident council — the desk's backlog read as decisions.
 *
 * ## What an incident desk is actually for
 *
 * Not counting. A register that only counts produces the sentence "there were sixteen
 * incidents", which nobody can act on. The three questions a desk has are: what is rotting
 * in the queue, where is this concentrated, and what can we actually stand behind if
 * challenged. Those are the three families of finding here.
 *
 * ## The two claims this council refuses to make
 *
 * **It never treats an unverified incident as false.** UNVERIFIED means nobody has checked,
 * which is a workload statement about the desk and not an evidential one about the report.
 * Conflating them is how a real incident gets quietly dropped, and it is the exact failure a
 * register exists to prevent.
 *
 * **It never infers a perpetrator, a party or a pattern of intent from the data.** The rows
 * hold what was reported and where; they do not hold who caused it. A concentration finding
 * says "this ward carries most of it", never "this ward is being targeted".
 */
export function advise(b: Board): Finding[] {
  const out: Finding[] = []
  const s = b.summary
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const pct = (n: number | null | undefined) =>
    n == null ? '—' : `${Math.round(n * 100)}%`

  // ── what is rotting in the queue ───────────────────────────────────────────
  if (s.openOlderThan48h > 0) {
    const share = s.total > 0 ? s.openOlderThan48h / s.total : 0
    out.push({
      id: 'ageing',
      severity: share > 0.3 ? 'critical' : 'serious',
      headline: `${num(s.openOlderThan48h)} incidents have been open past the ageing threshold.`,
      evidence: `${num(s.open + s.investigating)} open or under investigation in total`
        + (s.medianHoursToAcknowledge != null
          ? `; median ${s.medianHoursToAcknowledge.toFixed(1)}h from report to first look.`
          : '; too few acknowledgements to compute a median.'),
      action: 'Assign each of these to a named person today. An incident nobody has touched in two days is not a low-priority incident — it is one the desk has lost, and it will read as negligence in any review of how the campaign handled the day.',
      votesAtStake: s.openOlderThan48h,
    })
  }

  const critical = s.bySeverity.CRITICAL ?? 0
  if (critical > 0) {
    const openCritical = Math.min(critical, s.open + s.investigating)
    out.push({
      id: 'critical',
      severity: 'critical',
      headline: `${num(critical)} incidents are recorded as critical`
        + (openCritical > 0 ? `, and up to ${num(openCritical)} of them are still open.` : '.'),
      evidence: `${num(s.totalDeaths)} deaths, ${num(s.totalInjuries)} injuries,`
        + ` ${num(s.totalArrests)} arrests, ${num(s.totalVictims)} people affected across the window.`,
      action: 'Severity is a description of harm, not of urgency — check each of these has an owner and an escalation route before working anything else. A critical incident sitting unassigned is the single worst state this register can be in.',
      votesAtStake: critical,
    })
  }

  // ── what the desk can actually stand behind ────────────────────────────────
  if (s.verifiedShare != null && s.verifiedShare < 0.6 && s.total > 0) {
    const unverified = Math.round(s.total * (1 - s.verifiedShare))
    out.push({
      id: 'verification',
      severity: s.verifiedShare < 0.35 ? 'serious' : 'watch',
      headline: `Only ${pct(s.verifiedShare)} of what this desk holds has been verified or corroborated.`,
      evidence: `About ${num(unverified)} of ${num(s.total)} incidents carry no verification.`,
      action: 'Unverified is not false — it means nobody has checked. But an unverified incident cannot be quoted publicly, cannot be handed to a body that can act, and will be the first thing an opponent attacks. Verify the critical ones before you verify the easy ones.',
      votesAtStake: unverified,
    })
  }

  if (s.escalated > 0) {
    out.push({
      id: 'escalated',
      severity: 'watch',
      headline: `${num(s.escalated)} incidents have been referred to a body that can act on them.`,
      evidence: `${num(s.resolved)} resolved and ${num(s.closed)} closed in the same window.`,
      action: 'Escalation transfers the work, not the responsibility. Each of these needs a date by which the desk expects an answer, or they become a list of things somebody else was told about once.',
      votesAtStake: 0,
    })
  }

  // ── where it is concentrated ───────────────────────────────────────────────
  const placed = b.geography.filter((g) => g.total > 0)
  if (placed.length >= 2) {
    const sorted = [...placed].sort((a, b2) => b2.total - a.total)
    const total = sorted.reduce((n, g) => n + g.total, 0)
    const top = sorted.slice(0, Math.min(3, sorted.length))
    const share = total > 0 ? top.reduce((n, g) => n + g.total, 0) / total : 0
    if (share > 0.6) {
      out.push({
        id: 'concentration',
        severity: 'watch',
        headline: `${pct(share)} of everything reported sits in ${top.length === 1 ? 'one area' : `${top.length} areas`}.`,
        evidence: top.map((g) => `${g.unitName ?? g.unitPath} — ${num(g.total)} incidents`
          + (g.critical > 0 ? `, ${num(g.critical)} critical` : '')
          + (g.deaths > 0 ? `, ${num(g.deaths)} deaths` : '')).join(' · '),
        action: 'Put your observers and your legal support where the reports already are, rather than spreading them evenly. This says where reports are COMING FROM — which is not the same as where incidents are happening, and a quiet area may simply have nobody reporting.',
        votesAtStake: top.reduce((n, g) => n + g.total, 0),
        cellCodes: top.map((g) => g.unitName ?? g.unitPath),
      })
    }
  }

  // ── what kind of thing this is ─────────────────────────────────────────────
  const cats = Object.entries(s.byCategory).sort((a, b2) => b2[1] - a[1])
  const topCat = cats[0]
  if (topCat && s.total > 0 && topCat[1] / s.total > 0.25) {
    out.push({
      id: 'category',
      severity: 'watch',
      headline: `${label(topCat[0])} is ${pct(topCat[1] / s.total)} of everything on the desk.`,
      evidence: cats.slice(0, 4).map(([c, n]) => `${label(c)} ${num(n)}`).join(' · '),
      action: 'One category dominating is a brief for a specific countermeasure rather than for general vigilance. Decide whether the response is legal, operational or communications — they are three different teams, and treating a category problem as a volume problem gets none of them deployed.',
      votesAtStake: topCat[1],
      cellCodes: cats.slice(0, 5).map(([c]) => label(c)),
    })
  }

  // ── is it accelerating ─────────────────────────────────────────────────────
  /*
     Compared over the two halves of the window shown, not against a fixed 7/30 split.

     `last7Days` and `last30Days` describe the server's own windows and disagree with the
     range control the reader is looking at — reporting "up 40% this week" on a board
     filtered to 90 days is a claim about a different chart.
  */
  if (b.trend.length >= 6) {
    const half = Math.floor(b.trend.length / 2)
    const early = b.trend.slice(0, half).reduce((n, p) => n + p.total, 0)
    const late = b.trend.slice(half).reduce((n, p) => n + p.total, 0)
    if (early > 0 && late > early * 1.4) {
      out.push({
        id: 'trend',
        severity: late > early * 2 ? 'serious' : 'watch',
        headline: `Reporting is accelerating — ${num(late)} in the second half of this window against ${num(early)} in the first.`,
        evidence: `${b.trend.length} days shown; peak day ${num(Math.max(...b.trend.map((p) => p.total)))}.`,
        action: 'Rising counts can mean rising incidents or a desk that has got better at capturing them, and the two need opposite responses. Check whether new reporters or new channels came online in this window before treating it as a deterioration on the ground.',
        votesAtStake: late - early,
      })
    } else if (late > 0 && early > late * 1.6) {
      out.push({
        id: 'trend',
        severity: 'good',
        headline: `Reporting has fallen — ${num(late)} in the second half against ${num(early)} in the first.`,
        evidence: `${b.trend.length} days shown.`,
        action: 'Before treating this as improvement, check that your reporters are still active. A register going quiet because nobody is filing looks exactly like a register going quiet because nothing is happening.',
        votesAtStake: 0,
      })
    }
  }

  // ── how fast the desk moves ────────────────────────────────────────────────
  if (s.medianHoursToResolve != null) {
    out.push({
      id: 'response',
      severity: s.p90HoursToResolve != null && s.p90HoursToResolve > s.medianHoursToResolve * 4
        ? 'serious' : 'watch',
      headline: `Half of what gets resolved takes under ${s.medianHoursToResolve.toFixed(1)} hours`
        + (s.p90HoursToResolve != null
          ? `, but the slowest tenth take over ${s.p90HoursToResolve.toFixed(1)}.` : '.'),
      evidence: `${num(b.times?.resolvedCount ?? 0)} resolved, ${num(b.times?.acknowledgedCount ?? 0)} acknowledged in this window.`,
      action: 'The median is a comfortable number and the p90 is the one that gets written about. Look at what the slow tail has in common — a category, an area, one assignee — because it is almost never randomly distributed.',
      votesAtStake: 0,
    })
  }

  // ── who is telling us ──────────────────────────────────────────────────────
  if (b.sources.length > 0) {
    const total = b.sources.reduce((n, x) => n + x.total, 0)
    const top = [...b.sources].sort((a, b2) => b2.total - a.total)[0]!
    if (total > 0 && top.total / total > 0.5) {
      out.push({
        id: 'sources',
        severity: 'watch',
        headline: `${pct(top.total / total)} of what the desk knows comes from ${top.outlet}.`,
        evidence: b.sources.slice(0, 4).map((x) => `${x.outlet} ${num(x.total)}`).join(' · '),
        action: 'A single dominant source is a single point of failure and a single editorial lens. It does not make the reports wrong; it does mean the register describes what that source covers, and the gaps in its coverage are invisible from inside this page.',
        votesAtStake: 0,
      })
    }
  }

  if (out.length === 0 && s.total > 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing on this desk crosses a threshold.',
      evidence: `${num(s.total)} incidents in the window, ${num(s.open + s.investigating)} still open,`
        + ` ${pct(s.verifiedShare)} verified.`,
      action: 'No action. Widen the range if you are checking whether something has been building — a quiet fortnight inside a bad quarter looks identical from here.',
      votesAtStake: 0,
    })
  }

  return rank(out)
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'ageing', words: ['stale', 'old', 'ageing', 'aging', 'backlog', 'queue', 'stuck', 'overdue', 'waiting'] },
  { id: 'critical', words: ['critical', 'worst', 'urgent', 'serious', 'death', 'killed', 'injur', 'violence', 'first'] },
  { id: 'verification', words: ['verif', 'trust', 'prove', 'evidence', 'stand up', 'publish', 'quote', 'credib'] },
  { id: 'escalated', words: ['escalat', 'refer', 'police', 'iebc', 'authorit', 'report to'] },
  { id: 'concentration', words: ['where', 'area', 'county', 'ward', 'geograph', 'hotspot', 'concentrat', 'location', 'map'] },
  { id: 'category', words: ['category', 'kind', 'type', 'bribery', 'what sort', 'pattern'] },
  { id: 'trend', words: ['trend', 'rising', 'falling', 'worse', 'better', 'increas', 'decreas', 'accelerat'] },
  { id: 'response', words: ['response time', 'how fast', 'speed', 'resolve', 'slow', 'median', 'sla'] },
  { id: 'sources', words: ['source', 'outlet', 'media', 'who told', 'reported by', 'press'] },
  { id: 'quiet', words: ['quiet', 'anything', 'all clear', 'ok', 'fine'] },
]

export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}

/**
 * The desk, written out for the model.
 *
 * The `ASK` playbook assembles no brief of its own, so this text IS everything the council
 * knows. Two rules are stated as instructions because a model given raw counts will
 * otherwise break both: unverified is not false, and a concentration of REPORTS is not a
 * concentration of incidents.
 */
export function incidentContext(b: Board, findings: Finding[]): string {
  const s = b.summary
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const breakdown = (r: Record<string, number>) =>
    Object.entries(r).sort((a, c) => c[1] - a[1])
      .map(([k, v]) => `${label(k)} ${v}`).join(', ') || 'none'

  return [
    "You are advising a Kenyan campaign's election-incident desk. Answer in plain language a",
    'campaign manager or legal lead can act on. Use ONLY the figures below. Two rules you must',
    'not break:',
    '(1) UNVERIFIED means nobody has checked yet. It does NOT mean the report is false, and you',
    'may not describe unverified incidents as doubtful or exaggerated.',
    '(2) These counts are of REPORTS. A quiet area may have no reporters rather than no',
    'incidents, and you must not infer who caused anything — the register does not hold that.',
    '',
    `WINDOW: ${b.days === 0 ? 'all time' : `last ${b.days} days`}.`,
    `TOTALS: ${num(s.total)} incidents — ${num(s.open)} open, ${num(s.investigating)} investigating,`
      + ` ${num(s.escalated)} escalated, ${num(s.resolved)} resolved, ${num(s.closed)} closed,`
      + ` ${num(s.disputed)} disputed. ${num(s.openOlderThan48h)} open past the ageing threshold.`,
    `SEVERITY: ${breakdown(s.bySeverity)}`,
    `CATEGORY: ${breakdown(s.byCategory)}`,
    `VERIFICATION: ${breakdown(s.byVerification)}`
      + (s.verifiedShare == null ? '' : ` (verified or corroborated share ${Math.round(s.verifiedShare * 100)}%)`),
    `HARM: ${num(s.totalDeaths)} deaths, ${num(s.totalInjuries)} injuries, ${num(s.totalArrests)} arrests,`
      + ` ${num(s.totalVictims)} people affected, ${num(s.totalVoterImpact)} voters affected.`,
    `SPEED: median ${s.medianHoursToAcknowledge?.toFixed(1) ?? 'n/a'}h to acknowledge,`
      + ` median ${s.medianHoursToResolve?.toFixed(1) ?? 'n/a'}h to resolve,`
      + ` p90 ${s.p90HoursToResolve?.toFixed(1) ?? 'n/a'}h.`,
    '',
    'WHERE (area, incidents, critical, deaths):',
    ...b.geography.slice(0, 10).map((g) => `- ${g.unitName ?? g.unitPath}: ${g.total},`
      + ` ${g.critical} critical, ${g.deaths} deaths`),
    '',
    `SOURCES: ${b.sources.slice(0, 6).map((x) => `${x.outlet} ${x.total}`).join(', ') || 'none recorded'}`,
    b.trend.length > 0
      ? `DAILY TREND (${b.trend.length} days): ${b.trend.map((p) => p.total).join(',')}`
      : 'DAILY TREND: no days in window.',
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].join('\n')
}
